import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowLeft,
  Camera,
  Check,
  ChevronDown,
  Gift,
  Loader2,
  ReceiptText,
  ScanLine,
} from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toastHelpers } from '@/lib/toast-helpers'
import { formatCurrency, formatRelative } from '@/lib/utils'
import { cameraScanSupported } from '@/lib/scannerCamera'
import { useDashboardStream, type DashboardStreamEvent } from '@/lib/dashboardStream'
import {
  CameraCardScanner,
  ScanError,
  describeScanError,
} from '@/components/counter/loyaltyCardScanner'
import type { Order, OrderItem, ResolvedLoyaltyCard } from '@/types'

/**
 * What the guest actually ordered: voided lines are struck from the bill and
 * draft lines were never fired, so neither helps staff recognise an order.
 * Same filter as the counter rail's SentItemsSection. Exported for tests.
 */
export function visibleOrderItems(order: Order): OrderItem[] {
  return (order.items ?? []).filter((i) => i.status !== 'voided' && i.status !== 'draft')
}

/**
 * If attaching this card would silently replace a DIFFERENT guest already on
 * the order, return that guest's label so the confirm screen can warn and the
 * button can demand a deliberate "Replace". Null means a plain attach: no
 * guest on the order, or the card belongs to the same person (phone match —
 * loyalty identity is phone-keyed; compared digits-only since order phones
 * can arrive formatted from the till). An order with a name but no phone
 * can't be matched, so it warns — replacing is the risky path. Exported for
 * tests.
 */
export function replacedGuestLabel(order: Order, card: ResolvedLoyaltyCard): string | null {
  const existing = (order.customer_name || order.customer_phone || '').trim()
  if (!existing) return null
  const digits = (s: string) => s.replace(/\D/g, '')
  const orderPhone = digits(order.customer_phone ?? '')
  const cardPhone = digits(card.customer_phone ?? '')
  if (orderPhone && cardPhone && orderPhone === cardPhone) return null
  return existing
}

type Step =
  | { name: 'pick' }
  | { name: 'scan'; order: Order }
  | { name: 'confirm'; order: Order; card: ResolvedLoyaltyCard }
  | { name: 'done'; order: Order; card: ResolvedLoyaltyCard }

/**
 * Dashboard-stream events that can change the open-orders picker: a new order
 * appears (created), leaves (completed/cancelled/paid/voided), or changes in
 * a way worth repainting (updated — covers guest attaches from other devices).
 * Table/online events deliberately excluded: they can't alter this list.
 * Exported for tests.
 */
export function isScanListEvent(type: string): boolean {
  return (
    type === 'order_created' ||
    type === 'order_updated' ||
    type === 'order_completed' ||
    type === 'order_cancelled' ||
    type === 'order_voided' ||
    type === 'payment'
  )
}

/**
 * The staff-phone scanning flow: pick an open order → scan the guest's wallet
 * QR → confirm the attach. Three screens, phone-sized.
 *
 * ATTACH-ONLY BY DESIGN. This page never writes a punch: it calls the same
 * two endpoints the till uses (resolve-card, then the guest PATCH) and the
 * punch happens inside the payment TX when the bill is closed. UNIQUE(order_id)
 * on loyalty_punches means a phone attach plus a receipt-QR claim on the same
 * order can never double-punch.
 *
 * The order picker is the safety-critical screen — there is no terminal
 * pairing, so choosing the right order IS the pairing. Rows carry order
 * number, type, total, age and any already-attached guest so a wrong-order
 * attach is hard to do silently.
 */
export function StaffScanPage() {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<Step>({ name: 'pick' })

  // Per-venue gate. The admin toggle lives in loyalty settings; this thin
  // probe is counter-accessible. While loading, show the spinner rather than
  // flashing the disabled screen at every open.
  const flagQuery = useQuery({
    queryKey: ['phone-scan-enabled'],
    queryFn: () => apiClient.getPhoneScanEnabled(),
    staleTime: 60_000,
  })

  // Realtime: same SSE stream the till consumes (counter role is allowed on
  // it). A rung-up order appears here within ~1s of the till creating it. The
  // hook reads onEvent through a ref, so closing over `step` here is fine.
  const streamStatus = useDashboardStream({
    onEvent: (ev: DashboardStreamEvent) => {
      if (isScanListEvent(ev.type)) {
        queryClient.invalidateQueries({ queryKey: ['staffScanOpenOrders'] })
      }
      // The order on the scan/confirm screen just got paid on the till —
      // attaching is now pointless (the punch path already ran without this
      // guest). Bounce back to the picker instead of failing on attach.
      if (
        (ev.type === 'payment' || ev.type === 'order_completed') &&
        (step.name === 'scan' || step.name === 'confirm') &&
        ev.order_id === step.order.id
      ) {
        toastHelpers.error(
          'Order was just paid',
          `#${step.order.order_number} closed on the till — the guest can scan the receipt QR instead.`,
        )
        setStep({ name: 'pick' })
      }
    },
  })

  // Open (unpaid) orders only — the natural encoding of "scan before payment".
  // SSE above is the primary freshness signal; the poll is the safety net for
  // a silently dead stream (60s while live, 15s otherwise — the till's idiom).
  const ordersQuery = useQuery({
    queryKey: ['staffScanOpenOrders'],
    queryFn: async () => {
      const r = await apiClient.getOrders({ per_page: 100, open_only: true })
      const list = Array.isArray(r.data) ? r.data : []
      // Newest first — the order being rung up right now is the common case.
      return [...list].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
    },
    refetchInterval: streamStatus === 'live' ? 60_000 : 15_000,
    enabled: flagQuery.data?.data?.enabled === true,
  })

  if (flagQuery.isLoading) {
    return (
      <Shell>
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Shell>
    )
  }

  if (!flagQuery.data?.data?.enabled) {
    return (
      <Shell>
        <div className="mx-auto max-w-sm space-y-3 px-4 py-16 text-center">
          <ScanLine className="mx-auto h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Phone scanning is off</h1>
          <p className="text-sm text-muted-foreground">
            Ask the owner to turn on “Staff phone scanning” under Marketing → Loyalty program (and
            make sure the loyalty programme itself is enabled).
          </p>
        </div>
      </Shell>
    )
  }

  if (step.name === 'pick') {
    return (
      <Shell title="Attach a loyalty card">
        <OrderPicker
          orders={ordersQuery.data ?? []}
          loading={ordersQuery.isLoading}
          onPick={(order) => setStep({ name: 'scan', order })}
        />
      </Shell>
    )
  }

  if (step.name === 'scan') {
    return (
      <Shell
        title={`Scan for #${step.order.order_number}`}
        onBack={() => setStep({ name: 'pick' })}
      >
        <ScanStep
          order={step.order}
          onResolved={(card) => setStep({ name: 'confirm', order: step.order, card })}
        />
      </Shell>
    )
  }

  if (step.name === 'confirm') {
    return (
      <Shell
        title="Confirm"
        onBack={() => setStep({ name: 'scan', order: step.order })}
      >
        <ConfirmStep
          order={step.order}
          card={step.card}
          onAttached={() => {
            // The till's own open-orders strip refetches on SSE/polling; this
            // refresh keeps THIS phone's picker honest for the next scan.
            queryClient.invalidateQueries({ queryKey: ['staffScanOpenOrders'] })
            setStep({ name: 'done', order: step.order, card: step.card })
          }}
        />
      </Shell>
    )
  }

  return (
    <Shell>
      <DoneStep
        order={step.order}
        card={step.card}
        onAgain={() => setStep({ name: 'pick' })}
      />
    </Shell>
  )
}

/** Page chrome: safe-area padding, centered column, optional back button. */
function Shell({
  title,
  onBack,
  children,
}: {
  title?: string
  onBack?: () => void
  children: React.ReactNode
}) {
  return (
    <div className="min-h-full bg-background pt-[env(safe-area-inset-top,0px)]">
      <div className="mx-auto flex min-h-full w-full max-w-md flex-col">
        {title && (
          <header className="flex items-center gap-2 border-b border-border/70 px-4 py-3">
            {onBack && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 shrink-0 px-0"
                onClick={onBack}
                aria-label="Back"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <h1 className="min-w-0 truncate text-base font-semibold">{title}</h1>
          </header>
        )}
        <div className="flex-1 px-4 py-4">{children}</div>
      </div>
    </div>
  )
}

function OrderPicker({
  orders,
  loading,
  onPick,
}: {
  orders: Order[]
  loading: boolean
  onPick: (order: Order) => void
}) {
  // Tap a card to open its item list; the scan action lives inside the
  // expanded area. One card open at a time keeps a 100-order list scannable.
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="space-y-3 py-12 text-center">
        <ReceiptText className="mx-auto h-10 w-10 text-muted-foreground" />
        <h2 className="text-base font-semibold">No open orders</h2>
        <p className="mx-auto max-w-xs text-sm text-muted-foreground">
          Ring the order up first — if it's already paid, the guest can scan the receipt QR
          instead. Don't see the order? Finish creating it on the till.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Pick the guest's order, then scan their wallet card. The visit is added when the order is
        paid.
      </p>
      <ul className="space-y-1.5">
        {orders.map((o) => {
          const items = visibleOrderItems(o)
          const expanded = expandedId === o.id
          return (
            <li
              key={o.id}
              className="rounded-lg border border-border/80 bg-background shadow-sm"
            >
              <button
                type="button"
                onClick={() => setExpandedId(expanded ? null : o.id)}
                aria-expanded={expanded}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted/40 active:bg-muted/60"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-sm font-semibold">#{o.order_number}</span>
                    <span className="text-xs capitalize text-muted-foreground">
                      {o.order_type.replace(/_/g, ' ')}
                    </span>
                    <span className="ml-auto text-sm font-semibold tabular-nums">
                      {formatCurrency(o.total_amount)}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      {items.length
                        ? `${items.length} item${items.length === 1 ? '' : 's'} · `
                        : ''}
                      {formatRelative(o.created_at)}
                    </span>
                    {(o.customer_name || o.customer_phone) && (
                      <span className="truncate rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                        {o.customer_name || o.customer_phone}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                    expanded ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expanded && (
                <div className="space-y-2 border-t border-border/60 px-3 pb-3 pt-2">
                  {items.length > 0 && (
                    <ul className="space-y-1">
                      {items.map((item) => (
                        <li key={item.id} className="flex items-baseline gap-2 text-sm">
                          <span className="min-w-0 flex-1 truncate">
                            <span className="tabular-nums text-muted-foreground">
                              {item.quantity} ×{' '}
                            </span>
                            {item.product?.name ?? 'Item'}
                            {item.special_instructions && (
                              <span className="block truncate text-xs text-muted-foreground">
                                {item.special_instructions}
                              </span>
                            )}
                          </span>
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                            {formatCurrency(item.total_price)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button type="button" className="w-full" onClick={() => onPick(o)}>
                    <ScanLine className="mr-2 h-4 w-4" />
                    Scan loyalty card
                  </Button>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function ScanStep({
  order,
  onResolved,
}: {
  order: Order
  onResolved: (card: ResolvedLoyaltyCard) => void
}) {
  // Camera opens immediately — on a phone this page exists to point the
  // camera at a card. The typed-code path stays for USB wedges and dead
  // cameras (same dual input as the till).
  const [camera, setCamera] = useState(() => cameraScanSupported())
  const [code, setCode] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const resolveMutation = useMutation({
    mutationFn: async (rawToken: string) => {
      const token = rawToken.trim()
      if (!token) throw new Error('empty')
      const res = await apiClient.resolveLoyaltyCard(token)
      const card = res.data
      if (!card || !card.enabled) {
        throw new ScanError('loyalty_off', 'Loyalty is off — enable it in admin settings.')
      }
      if (!card.customer_phone) {
        throw new ScanError('no_phone', 'This card has no phone on file to attach.')
      }
      return card
    },
    onSuccess: (card) => {
      setCode('')
      onResolved(card)
    },
    onError: (err) => {
      const { title, description } = describeScanError(err)
      toastHelpers.error(title, description)
      setCode('')
      // Re-arm the camera for another try (a decode closes it).
      if (cameraScanSupported()) setCamera(true)
    },
  })

  function submit() {
    if (resolveMutation.isPending || !code.trim()) return
    resolveMutation.mutate(code)
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border/80 bg-muted/30 px-3 py-2 text-sm">
        <span className="font-mono font-semibold">#{order.order_number}</span>
        <span className="text-muted-foreground">
          {' '}
          · {formatCurrency(order.total_amount)}
          {order.items?.length
            ? ` · ${order.items.length} item${order.items.length === 1 ? '' : 's'}`
            : ''}
        </span>
      </div>

      {camera && (
        <CameraCardScanner
          onDecode={(token) => {
            setCamera(false)
            resolveMutation.mutate(token)
          }}
          onClose={() => setCamera(false)}
          busy={resolveMutation.isPending}
        />
      )}

      <div className="flex items-center gap-1.5">
        <Input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          className="h-10 font-mono text-sm"
          placeholder="Or enter card code"
          autoComplete="off"
          spellCheck={false}
          disabled={resolveMutation.isPending}
        />
        <Button
          type="button"
          className="h-10 shrink-0"
          onClick={submit}
          disabled={resolveMutation.isPending || !code.trim()}
        >
          {resolveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Look up'}
        </Button>
      </div>

      {!camera && cameraScanSupported() && (
        <Button
          type="button"
          variant="outline"
          className="w-full gap-1.5"
          onClick={() => setCamera(true)}
          disabled={resolveMutation.isPending}
        >
          <Camera className="h-4 w-4" />
          Open camera
        </Button>
      )}

      <p className="text-xs leading-snug text-muted-foreground">
        Point the camera at the guest's Apple/Google Wallet loyalty card.
      </p>
    </div>
  )
}

function ConfirmStep({
  order,
  card,
  onAttached,
}: {
  order: Order
  card: ResolvedLoyaltyCard
  onAttached: () => void
}) {
  const replacing = replacedGuestLabel(order, card)
  const attachMutation = useMutation({
    mutationFn: async () => {
      // Same guest endpoint the till uses; the punch stays with the payment
      // path. If the order got paid between pick and confirm it is no longer
      // open server-side and the attach fails cleanly below.
      const res = await apiClient.updateCounterOrderGuest(order.id, {
        customer_phone: card.customer_phone as string,
        customer_name: card.customer_name || undefined,
      })
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Could not attach the customer')
      }
      return res.data
    },
    onSuccess: onAttached,
    onError: (err) => {
      const { title, description } = describeScanError(err)
      toastHelpers.error(title, description)
    },
  })

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-lg border border-border/80 p-3">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Order
        </div>
        <div className="text-sm">
          <span className="font-mono font-semibold">#{order.order_number}</span>
          <span className="text-muted-foreground">
            {' '}
            · {formatCurrency(order.total_amount)}
            {order.items?.length
              ? ` · ${order.items.length} item${order.items.length === 1 ? '' : 's'}`
              : ''}
          </span>
        </div>
      </div>

      <div className="space-y-2 rounded-lg border border-border/80 p-3">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Guest
        </div>
        <div className="text-sm font-semibold">{card.customer_name || 'Loyalty member'}</div>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Gift className="h-4 w-4" />
          <span className="tabular-nums">
            {card.punches}/{card.threshold}
          </span>
          <span>visits{card.reward_label ? ` toward ${card.reward_label}` : ''}</span>
        </div>
      </div>

      {replacing && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            This order is already linked to <span className="font-semibold">{replacing}</span> —
            attaching will replace them, and the visit at payment goes to the new guest.
          </span>
        </div>
      )}

      <Button
        type="button"
        variant={replacing ? 'destructive' : 'default'}
        className="w-full gap-1.5"
        onClick={() => attachMutation.mutate()}
        disabled={attachMutation.isPending}
      >
        {attachMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Check className="h-4 w-4" />
        )}
        {replacing ? `Replace guest on #${order.order_number}` : `Attach to #${order.order_number}`}
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        The visit will be added when the order is paid.
      </p>
    </div>
  )
}

function DoneStep({
  order,
  card,
  onAgain,
}: {
  order: Order
  card: ResolvedLoyaltyCard
  onAgain: () => void
}) {
  // Re-arm quickly during a rush: the success screen self-advances back to the
  // picker, but stays long enough to read.
  useEffect(() => {
    const t = setTimeout(onAgain, 8000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-4 py-10 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/60">
        <Check className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div>
        <h2 className="text-lg font-semibold">
          {card.customer_name || 'Guest'} attached to #{order.order_number}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {card.punches}/{card.threshold} visits
          {card.reward_label ? ` toward ${card.reward_label}` : ''} — the visit will be added when
          the order is paid.
        </p>
      </div>
      <Button type="button" variant="outline" className="gap-1.5" onClick={onAgain}>
        <ScanLine className="h-4 w-4" />
        Scan another
      </Button>
    </div>
  )
}
